import type { PostPlacement, PostPlacementInstance } from "building-types/settings/railingTypes";
import { PostPlacementType } from "building-types/settings/railingTypes";
import type { Table } from "common/data/table";
import { polyLine2EdgeEval, polyLine2EdgeLength, polyLine2EdgeTangent } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rangeInclusiveByMinStep, rangeInclusiveByStep } from "geom/range";

export function postPointsFromPostPlacements(postPlacements: Table<PostPlacement>, edgeCount: number, path: PolyLine2) {
  const points: PostPlacementInstance[] = [];
  for (const id in postPlacements) {
    const postPlacement = postPlacements[id];

    // if the edge index is out of bounds, skip it
    if (postPlacement.edgeIndex !== undefined && postPlacement.edgeIndex > edgeCount - 1) {
      continue;
    }

    switch (postPlacement.type) {
      case PostPlacementType.PARAM: {
        const { edgeIndex, param, paramNormalized, removeInfillBefore, removeInfillAfter } = postPlacement;

        if (edgeIndex === undefined) {
          for (let i = 0; i < edgeCount; i++) {
            addPostPoint({
              param,
              paramNormalized,
              edgeLength: polyLine2EdgeLength(path, i),
              path,
              index: i,
              points,
              id,
              removeInfillBefore,
              removeInfillAfter,
            });
          }
        } else {
          const edgeLength = polyLine2EdgeLength(path, edgeIndex);
          addPostPoint({
            param,
            paramNormalized,
            edgeLength,
            path,
            index: edgeIndex,
            points,
            id,
            removeInfillBefore,
            removeInfillAfter,
          });
        }
        break;
      }
      case PostPlacementType.ARRAY: {
        const { edgeIndex, spacing, shift, maxCount: limit } = postPlacement;

        if (spacing === 0) {
          break;
        }

        if (edgeIndex === undefined) {
          for (let i = 0; i < edgeCount; i++) {
            let placedCount: number | undefined = 0;
            const edgeLength = polyLine2EdgeLength(path, i);
            const spaceToFill = edgeLength - shift;
            const count = Math.ceil(spaceToFill / spacing);
            for (let j = 0; j < count; j++) {
              const param = shift + spacing * j;
              if (param > 0 && param < edgeLength) {
                if (limit !== undefined && placedCount !== undefined && placedCount >= limit) {
                  continue;
                }
                placedCount = addPostPoint({
                  param,
                  paramNormalized: false,
                  edgeLength,
                  path,
                  index: i,
                  points,
                  placed: placedCount,
                  id,
                });
              }
            }
          }
        } else {
          let placedCount: number | undefined = 0;
          const edgeLength = polyLine2EdgeLength(path, edgeIndex);
          const steps = rangeInclusiveByStep(shift, edgeLength, spacing);
          for (const param of steps) {
            if (param > 0 && param < edgeLength) {
              if (limit !== undefined && placedCount !== undefined && placedCount >= limit) {
                continue;
              }
              placedCount = addPostPoint({
                param,
                paramNormalized: false,
                edgeLength,
                path,
                index: edgeIndex,
                points,
                placed: placedCount,
                id,
              });
            }
          }
        }
        break;
      }
      case PostPlacementType.DIVISION: {
        const { edgeIndex, segments, includeStartPost } = postPlacement;
        const startIndex = includeStartPost ? 0 : 1;
        if (edgeIndex === undefined) {
          for (let i = 0; i < edgeCount; i++) {
            const edgeLength = polyLine2EdgeLength(path, i);
            for (let j = startIndex; j < segments; j++) {
              const param = (1 / segments) * j;
              addPostPoint({
                param: param * edgeLength,
                paramNormalized: false,
                edgeLength,
                path,
                index: i,
                points,
                id,
              });
            }
          }
        } else {
          for (let j = startIndex; j < segments; j++) {
            const param = (1 / segments) * j;
            const edgeLength = polyLine2EdgeLength(path, edgeIndex);
            addPostPoint({
              param: param * edgeLength,
              paramNormalized: false,
              edgeLength,
              path,
              index: edgeIndex,
              points,
              id,
            });
          }
        }
        break;
      }
      case PostPlacementType.MAX_DISTANCE: {
        const { edgeIndex, maxDistance, includeStartPost } = postPlacement;
        if (edgeIndex === undefined) {
          for (let i = 0; i < edgeCount; i++) {
            const edgeLength = polyLine2EdgeLength(path, i);
            const steps = rangeInclusiveByMinStep(0, edgeLength, maxDistance);
            for (const stepIndex of Object.keys(steps)) {
              if (+stepIndex === steps.length - 1) {
                continue;
              }
              if (!includeStartPost) {
                if (+stepIndex === 0) {
                  continue;
                }
              }
              const param = steps[stepIndex];
              if (param >= 0 && param < edgeLength) {
                addPostPoint({
                  param,
                  paramNormalized: false,
                  edgeLength,
                  path,
                  index: i,
                  points,
                  id,
                });
              }
            }
          }
        } else {
          const edgeLength = polyLine2EdgeLength(path, edgeIndex);
          const steps = rangeInclusiveByMinStep(0, edgeLength, maxDistance);
          for (const stepIndex of Object.keys(steps)) {
            if (+stepIndex === steps.length - 1) {
              continue;
            }
            if (!includeStartPost) {
              if (+stepIndex === 0) {
                continue;
              }
            }
            const param = steps[stepIndex];
            if (param > 0 && param < edgeLength) {
              addPostPoint({
                param,
                paramNormalized: false,
                edgeLength,
                path,
                index: edgeIndex,
                points,
                id,
              });
            }
          }
        }
        break;
      }
    }
  }

  return points;
}

interface AddPostPointOptions {
  readonly points: PostPlacementInstance[];
  readonly path: PolyLine2;
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly edgeLength: number;
  readonly index: number;
  readonly id: string;
  placed?: number;
  readonly removeInfillBefore?: boolean;
  readonly removeInfillAfter?: boolean;
}

function addPostPoint({
  param,
  paramNormalized,
  edgeLength,
  path,
  index,
  points,
  placed,
  id,
  removeInfillBefore,
  removeInfillAfter,
}: AddPostPointOptions) {
  const absParam = Math.abs(param);
  if (paramNormalized) {
    if (absParam > 1) {
      return undefined;
    }
  } else {
    if (absParam > edgeLength) {
      return undefined;
    }
  }

  if (param < 0) {
    if (placed !== undefined) {
      placed++;
    }
    const paramWrapped = paramNormalized ? (1 + param) * edgeLength : edgeLength + param;

    const [x, y] = polyLine2EdgeEval(path, index, paramWrapped, false);
    const [tx, ty] = polyLine2EdgeTangent(path, index);
    points.push({
      x,
      y,
      tx,
      ty,
      edgeIndex: index,
      param: paramWrapped,
      id,
      removeInfillBefore: !!removeInfillBefore,
      removeInfillAfter: !!removeInfillAfter,
    });
  } else {
    if (placed !== undefined) {
      placed++;
    }
    const [x, y] = polyLine2EdgeEval(path, index, param, paramNormalized);
    const [tx, ty] = polyLine2EdgeTangent(path, index);
    points.push({
      edgeIndex: index,
      x,
      y,
      tx,
      ty,
      param: paramNormalized ? param * edgeLength : param,
      id,
      removeInfillBefore: !!removeInfillBefore,
      removeInfillAfter: !!removeInfillAfter,
    });
  }
  return placed;
}
